import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Save, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { isoToLocal, localToIso, openWardPdf } from "./wardUtils";
import { ChargesTable, PaymentsTable, TotalsCard } from "./WardBillParts";
import { PrintDocsMenu, DischargeDialog } from "./WardDocs";
import { Badge } from "@/components/ui/badge";
import { LogOut } from "lucide-react";

const DEFAULT_ITEMS = [
  { name: "Bed charge", unit: "day", qty: 1, rate: 0 },
  { name: "Oxygen charge", unit: "hour", qty: 0, rate: 0 },
];

const EMPTY = {
  patient_name: "", age: "", sex: "", ward: "PICU", bed_number: "", consultant: "", guardian: "", phone: "", diagnosis: "",
  admission_at: "", discharge_at: "", discount: 0, notes: "", items: DEFAULT_ITEMS, payments: [],
};

export default function WardBillEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [bill, setBill] = useState(id ? null : { ...EMPTY, admission_at: isoToLocal(new Date().toISOString()) });
  const [saving, setSaving] = useState(false);
  const [discharging, setDischarging] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.get(`/ward/bills/${id}`)
      .then(({ data }) => setBill({ ...data, admission_at: isoToLocal(data.admission_at), discharge_at: isoToLocal(data.discharge_at) }))
      .catch(() => { toast.error("Bill not found"); nav("/ward"); });
  }, [id, nav]);

  const set = (k) => (v) => setBill((b) => ({ ...b, [k]: v }));
  const field = (k) => ({ value: bill[k] ?? "", onChange: (e) => set(k)(e.target.value) });

  const save = async (thenPdf = false) => {
    if (!bill.patient_name?.trim()) { toast.error("Patient name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...bill, admission_at: localToIso(bill.admission_at), discharge_at: localToIso(bill.discharge_at),
        discount: Number(bill.discount || 0), items: bill.items.filter((i) => i.name?.trim()) };
      const { data } = id ? await api.put(`/ward/bills/${id}`, payload) : await api.post("/ward/bills", payload);
      toast.success(id ? "Saved" : `Patient admitted · ${data.bill_no}`);
      if (thenPdf) openWardPdf(data.id);
      if (!id) nav(`/ward/bills/${data.id}`, { replace: true });
      else setBill({ ...data, admission_at: isoToLocal(data.admission_at), discharge_at: isoToLocal(data.discharge_at) });
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!bill) return <div className="text-muted-foreground py-10 text-center">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="ward-bill-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => nav("/ward")} data-testid="ward-back"><ArrowLeft size={18} /></Button>
          <div>
            <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
              {id ? `${bill.patient_name} · ${bill.bill_no}` : "Admit patient"}
              {id && <Badge variant={bill.discharge_at ? "secondary" : "default"} data-testid="ward-status-badge">{bill.discharge_at ? `Discharged${bill.discharge_type ? ` (${bill.discharge_type})` : ""}` : "Admitted"}</Badge>}
            </h1>
            <p className="text-xs text-muted-foreground">Patient details, admission documents and billing. All charges are entered manually.</p>
          </div>
        </div>
        <div className="flex gap-2">
          {id && <PrintDocsMenu bill={bill} />}
          {id && <Button variant="outline" onClick={() => openWardPdf(id)} data-testid="ward-print"><FileText size={15} className="mr-1" /> Print bill</Button>}
          {id && !bill.discharge_at && <Button variant="outline" onClick={() => setDischarging(true)} data-testid="ward-discharge-btn"><LogOut size={15} className="mr-1" /> Discharge</Button>}
          <Button onClick={() => save(false)} disabled={saving} data-testid="ward-save" className="rounded-full">
            {saving ? <Loader2 size={15} className="animate-spin mr-1" /> : <Save size={15} className="mr-1" />} Save
          </Button>
        </div>
      </div>

      <section className="rounded-xl border border-border p-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="ward-patient-form">
        <div className="space-y-1.5 sm:col-span-2"><Label>Patient name *</Label><Input {...field("patient_name")} data-testid="ward-patient-name" placeholder="Full name" /></div>
        <div className="space-y-1.5"><Label>Age</Label><Input {...field("age")} data-testid="ward-age" placeholder="e.g. 4 yrs / 8 months" /></div>
        <div className="space-y-1.5"><Label>Sex</Label>
          <Select value={bill.sex || ""} onValueChange={set("sex")}>
            <SelectTrigger data-testid="ward-sex"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Ward</Label>
          <Select value={bill.ward} onValueChange={set("ward")}>
            <SelectTrigger data-testid="ward-ward"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="PICU">PICU</SelectItem><SelectItem value="General Ward">General Ward</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Bed no.</Label><Input {...field("bed_number")} data-testid="ward-bed" /></div>
        <div className="space-y-1.5"><Label>Consultant</Label><Input {...field("consultant")} data-testid="ward-consultant" /></div>
        <div className="space-y-1.5"><Label>Guardian / Phone</Label>
          <div className="flex gap-2"><Input {...field("guardian")} placeholder="Guardian" data-testid="ward-guardian" /><Input {...field("phone")} placeholder="Phone" data-testid="ward-phone" /></div>
        </div>
        <div className="space-y-1.5 sm:col-span-2"><Label>Admission date & time</Label><Input type="datetime-local" {...field("admission_at")} data-testid="ward-admission-at" /></div>
        <div className="space-y-1.5 sm:col-span-2"><Label>Discharge date & time</Label><Input type="datetime-local" {...field("discharge_at")} data-testid="ward-discharge-at" /></div>
        <div className="space-y-1.5 sm:col-span-2 lg:col-span-4"><Label>Diagnosis</Label><Input {...field("diagnosis")} data-testid="ward-diagnosis" /></div>
      </section>

      <ChargesTable items={bill.items} onChange={set("items")} />

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-6">
          <PaymentsTable payments={bill.payments} onChange={set("payments")} billId={id} />
          {id && bill.payments.some((p) => !p.id) && <p className="text-xs text-amber-600" data-testid="ward-receipt-hint">Save the bill to print receipts for new payments.</p>}
          <div className="space-y-1.5"><Label>Notes (printed on bill)</Label><Textarea rows={2} {...field("notes")} data-testid="ward-notes" /></div>
        </div>
        <TotalsCard bill={bill} onDiscount={set("discount")} />
      </div>
      {discharging && <DischargeDialog bill={bill} onClose={() => setDischarging(false)} onDone={(d) => { setDischarging(false); setBill((b) => ({ ...b, discharge_at: isoToLocal(d.discharge_at), discharge_type: d.discharge_type })); }} />}
    </div>
  );
}
